import { cva, type VariantProps } from 'class-variance-authority'
import { AlertTriangle, CheckCircle2, Info, XCircle } from 'lucide-react'
import type { ReactNode } from 'react'
import { cn } from '@/shared/utils/cn'

const alert = cva('flex gap-1.5 rounded border-l-4 p-2', {
  variants: {
    tone: {
      error: 'border-l-error-600 bg-error-50 text-text',
      warning: 'border-l-warning-600 bg-warning-50 text-text',
      info: 'border-l-info-600 bg-info-50 text-text',
      success: 'border-l-success-600 bg-success-50 text-text',
    },
  },
  defaultVariants: { tone: 'info' },
})

const ICONS = {
  error: XCircle,
  warning: AlertTriangle,
  info: Info,
  success: CheckCircle2,
} as const

const ICON_COLOURS = {
  error: 'text-error-600',
  warning: 'text-warning-700',
  info: 'text-info-600',
  success: 'text-success-600',
} as const

export interface AlertProps extends VariantProps<typeof alert> {
  tone?: keyof typeof ICONS
  title?: ReactNode
  children?: ReactNode
  className?: string
}

export function Alert({ tone = 'info', title, children, className }: AlertProps) {
  const Icon = ICONS[tone]

  return (
    <div
      // An error is announced immediately; anything else waits for a pause in
      // whatever the user is doing. Making everything role="alert" trains people
      // to ignore it.
      role={tone === 'error' ? 'alert' : 'status'}
      className={cn(alert({ tone }), className)}
    >
      <Icon aria-hidden="true" className={cn('mt-0.5 size-2.5 shrink-0', ICON_COLOURS[tone])} />
      <div className="min-w-0">
        {title && <p className="text-label font-semibold">{title}</p>}
        {/* Body text inherits the container's --color-text rather than going
         * muted. The Figma status tints are saturated enough that muted grey
         * lands at 4.16–4.46:1 on them — under AA. See the muted-on-tint note in
         * src/styles/themes/default.css. */}
        {children && <div className="text-small">{children}</div>}
      </div>
    </div>
  )
}
